import { useEffect } from 'react';
import { useT } from '../i18n';
import { ACTIONS, FIXED, keyLabel, keymap, useKeys } from '../keys/store';
import { useOverlay } from '../ui/overlays';

/*
    What the keyboard does, on the keyboard.

    The shortcuts were configurable from the start and invisible from the start: they lived in a
    settings tab, and nobody goes to a settings tab to find out that a feature exists. The people
    who would get the most out of them — somebody working through a hundred messages — are exactly
    the ones who never looked.

    So `?` opens this, the way it does in every application that has ever taught anybody a
    shortcut. It reads the same keymap the handlers do, so it cannot describe a binding that is
    not real: change the preset and this changes with it.
*/
export default function KeyGuide({ onClose }: { onClose: () => void }) {
    const { t } = useT();
    const preset = useKeys((s) => s.preset);
    const overrides = useKeys((s) => s.overrides);
    const map = keymap(preset, overrides);

    useOverlay();

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.key === '?') {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Grouped the way somebody thinks about them: where they are when they press the key.
    // Both kinds of shortcut under one heading each: a person looking for "how do I send" does
    // not care which of them the settings screen will let them change.
    const scopes = [...new Set([...ACTIONS.map((a) => a.scope), ...FIXED.map((f) => f.scope)])];

    return (
        <div
            data-testid="key-guide"
            data-print-hide
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 85,
                background: 'rgba(8, 7, 14, 0.72)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--sp-7)',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={t('keys.title')}
                style={{
                    maxWidth: 560,
                    width: '100%',
                    maxHeight: '100%',
                    overflowY: 'auto',
                    padding: 'var(--sp-7)',
                    background: 'var(--surface)',
                    border: '1px solid var(--border2)',
                    borderRadius: 'var(--r-card)',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.45)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--sp-6)' }}>
                    <div style={{ flex: 1, fontSize: 'var(--fs-lg)', fontWeight: 600 }}>{t('keys.title')}</div>
                    <button
                        onClick={onClose}
                        aria-label={t('app.close')}
                        style={{
                            minWidth: 44,
                            minHeight: 44,
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-dim)',
                            fontSize: 'var(--fs-xl)',
                        }}
                    >
                        ×
                    </button>
                </div>

                {scopes.map((scope) => (
                    <div key={scope} style={{ marginBottom: 'var(--sp-6)' }}>
                        <div className="kicker" style={{ marginBottom: 'var(--sp-3)' }}>// {t(scope)}</div>
                        <div style={{ display: 'grid', gap: 'var(--sp-1)' }}>
                            {ACTIONS.filter((a) => a.scope === scope).map((action) => (
                                <div
                                    key={action.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--sp-5)',
                                        padding: 'var(--sp-2) 0',
                                        fontSize: 'var(--fs-md)',
                                    }}
                                >
                                    <span style={{ flex: 1, minWidth: 0, color: 'var(--text-dim)' }}>
                                        {t(action.label)}
                                    </span>
                                    <kbd
                                        style={{
                                            fontFamily: 'var(--font-mono)',
                                            fontSize: 'var(--fs-2xs)',
                                            padding: 'var(--sp-1) var(--sp-3)',
                                            background: 'var(--surface2)',
                                            border: '1px solid var(--border2)',
                                            borderRadius: 'var(--r-badge)',
                                            color: 'var(--text)',
                                        }}
                                    >
                                        {keyLabel(map[action.id])}
                                    </kbd>
                                </div>
                            ))}
                            {FIXED.filter((f) => f.scope === scope).map((fixed) => (
                                <div
                                    key={fixed.keys}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--sp-5)',
                                        padding: 'var(--sp-2) 0',
                                        fontSize: 'var(--fs-md)',
                                    }}
                                >
                                    <span style={{ flex: 1, minWidth: 0, color: 'var(--text-dim)' }}>
                                        {t(fixed.label)}
                                    </span>
                                    <kbd
                                        style={{
                                            fontFamily: 'var(--font-mono)',
                                            fontSize: 'var(--fs-2xs)',
                                            padding: 'var(--sp-1) var(--sp-3)',
                                            background: 'var(--surface2)',
                                            border: '1px solid var(--border2)',
                                            borderRadius: 'var(--r-badge)',
                                            color: 'var(--text)',
                                        }}
                                    >
                                        {fixed.keys}
                                    </kbd>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>{t('keys.guideHint')}</div>
            </div>
        </div>
    );
}
